import type { ICellEditorComp, ICellEditorParams } from 'ag-grid-community'
import type { Iso2 } from 'intl-tel-input'
import { map } from 'remeda'
import {
  createTelInputController,
  parseCountryList,
  TEL_INPUT_VALIDATION_ERROR,
  type TelInputController,
  type TelInputValue
} from '../../../helpers/telephone'
import { extractEditorValue } from '../utils/editor-value'

type TelEditorParams = ICellEditorParams<
  Record<string, unknown>,
  unknown,
  unknown
> & {
  readonly required?: boolean
  readonly country?: string
  readonly locale?: string
  readonly onlyCountries?: readonly string[] | string
}

type TelEditorState =
  | { readonly status: 'Loading' }
  | { readonly status: 'Ready'; readonly controller: TelInputController }
  | { readonly status: 'Failure'; readonly error: unknown }
  | { readonly status: 'Destroyed' }

const countryCode = (value: string | undefined): Iso2 =>
  (value?.toLowerCase() ?? 'ua') as Iso2

const configuredCountries = (
  value: readonly string[] | string | undefined
): readonly Iso2[] =>
  typeof value === 'string'
    ? parseCountryList(value)
    : map(value ?? [], (country) => country.toLowerCase() as Iso2)

const initialValue = (params: TelEditorParams): string => {
  const value = extractEditorValue(params)
  return typeof value === 'string' ? value : ''
}

const isAcceptedValue = (value: TelInputValue, required: boolean): boolean =>
  value.status === 'valid' || (!required && value.status === 'empty')

const createRoot = (): HTMLDivElement => {
  const root = document.createElement('div')
  root.className = 'facade-grid-tel-editor'
  root.dataset.component = '.input'
  root.dataset.editor = 'tel'
  root.dataset.type = 'tel'
  root.dataset.size = 'sm'
  return root
}

export class FacadeTelCellEditor
  implements ICellEditorComp<Record<string, unknown>, unknown, unknown>
{
  private readonly gui = createRoot()
  private readonly input = document.createElement('input')
  private params?: TelEditorParams
  private state: TelEditorState = { status: 'Loading' }
  private cleanup?: () => void
  private focusRequested = false

  init(params: TelEditorParams): void {
    this.params = params
    this.input.type = 'tel'
    this.input.autocomplete = 'tel'
    this.input.value = initialValue(params)
    this.input.readOnly = true
    this.gui.append(this.input)

    const refreshValidation = (): void => params.validate()
    const onKeydown = (event: KeyboardEvent): void => {
      if (event.key === 'Enter') {
        event.preventDefault()
        event.stopPropagation()
        refreshValidation()
        if (this.valid()) {
          params.stopEditing()
        }
      }
    }
    this.input.addEventListener('input', refreshValidation)
    this.input.addEventListener('change', refreshValidation)
    this.input.addEventListener('countrychange', refreshValidation)
    this.input.addEventListener('keydown', onKeydown)
    const initialization = new AbortController()
    this.cleanup = () => {
      initialization.abort()
      this.input.removeEventListener('input', refreshValidation)
      this.input.removeEventListener('change', refreshValidation)
      this.input.removeEventListener('countrychange', refreshValidation)
      this.input.removeEventListener('keydown', onKeydown)
    }

    createTelInputController(this.input, {
      initialCountry: countryCode(params.country),
      locale: params.locale ?? document.documentElement.lang ?? 'uk',
      onlyCountries: configuredCountries(params.onlyCountries),
      dropdownParent: this.gui,
      signal: initialization.signal
    }).then(
      (controller) => {
        if (this.state.status === 'Destroyed') {
          controller.destroy()
          return
        }

        controller.setValue(initialValue(params))
        this.input.readOnly = false
        this.state = { status: 'Ready', controller }
        if (this.focusRequested) {
          controller.focus()
        }
        refreshValidation()
      },
      (error: unknown) => {
        if (this.state.status !== 'Destroyed') {
          this.input.readOnly = false
          this.state = { status: 'Failure', error }
          refreshValidation()
        }
      }
    )
  }

  getGui(): HTMLElement {
    return this.gui
  }

  afterGuiAttached(): void {
    this.focusRequested = true
    this.state.status === 'Ready'
      ? this.state.controller.focus()
      : this.input.focus()
  }

  getValue(): string | null {
    if (this.state.status !== 'Ready') {
      return null
    }

    const value = this.state.controller.getValue()
    return value.status === 'valid' ? value.value : null
  }

  isCancelAfterEnd(): boolean {
    return !this.valid()
  }

  isPopup(): boolean {
    return true
  }

  getPopupPosition(): 'under' {
    return 'under'
  }

  getValidationElement(): HTMLElement {
    return this.input
  }

  getValidationErrors(): string[] | null {
    return this.valid() ? null : [TEL_INPUT_VALIDATION_ERROR]
  }

  destroy(): void {
    this.cleanup?.()
    this.cleanup = undefined
    this.input.readOnly = false

    if (this.state.status === 'Ready') {
      this.state.controller.destroy()
    }

    this.state = { status: 'Destroyed' }
  }

  private valid(): boolean {
    const required = this.params?.required ?? false

    return (
      this.state.status === 'Ready' &&
      isAcceptedValue(this.state.controller.getValue(), required)
    )
  }
}

export type { TelEditorParams }
